import axios from 'axios';
import { cloneDeep } from 'lodash';
import { consentStatement } from './strings';
import { sendEvent } from '../../utils/analytics';
import {
  getAnalyticsParams,
  setStorageValue,
} from '../../utils/session-storage';
import fieldTypes from './field-types';
import { inferConsentTypes } from './helpers';

export const trimPostcode = (postcode) =>
  postcode.length > 3 ? postcode.slice(0, -3).trim() : postcode;

export const isPostcode = (string) => string.search(/\d/) > 0; // if string contains a number we consider it a postcode

const formatPayload = (payload, values, formFields) => {
  let includesFile = false;

  formFields.forEach(({ type, name, options }) => {
    if (type === fieldTypes.DATE_OF_BIRTH) {
      if (payload[name]) {
        // Only create a Date object if all date fields have values
        const { day, month, year } = values[name];
        if (day && month && year) {
          payload[name] = new Date(year, month - 1, day);
        } else {
          // For optional fields, send null if incomplete
          payload[name] = null;
        }
      }
    }

    if (type === fieldTypes.CHECKBOXES) {
      if (
        options.length === 1 &&
        payload[name] &&
        Array.isArray(payload[name])
      ) {
        payload[name] = Boolean(payload[name][0]);
      }
    }

    if (type === fieldTypes.FILE_UPLOAD && payload[name].files.length) {
      includesFile = true;
    }
  });

  if (payload.consent && Array.isArray(payload.consent)) {
    const consent = {
      statement: consentStatement,
    };

    inferConsentTypes(formFields).forEach((x) => {
      consent[x.value] = payload.consent.includes(x.value);
    });
    payload.consent = consent;

    // Stop form api returning a 422 error when there is empty optional address field
    formFields
      .filter(({ type }) => type === fieldTypes.ADDRESS)
      .forEach((addressType) => {
        if (
          payload[addressType.name] &&
          payload[addressType.name]?.address1 === '' &&
          payload[addressType.name]?.city === '' &&
          payload[addressType.name]?.postcode === ''
        ) {
          payload.consent.post = true;
        }
      });
  }

  // if payload contains an uploaded file convert the payload to a multipart/form-data object
  if (includesFile) {
    const formData = new FormData();

    Object.keys(payload).forEach((key) => {
      if (payload[key] && typeof payload[key] === 'object') {
        const isFileUploadObj =
          formFields.find((el) => el.name === key).type ===
          fieldTypes.FILE_UPLOAD;
        const payloadObj = isFileUploadObj ? payload[key].files : payload[key];
        Object.keys(payloadObj).forEach((subKey) => {
          // file upload array is expected to be 1-based by the forms api
          const payloadKey = isFileUploadObj ? parseInt(subKey) + 1 : subKey;
          formData.append(`${key}[${payloadKey}]`, payloadObj[subKey]);
        });
      } else {
        formData.append(key, payload[key]);
      }
    });

    return formData;
  }

  return payload;
};

const stripPayload = (payload, formFields) => {
  const fieldsToInclude = [
    'formName',
    'postcodeStub',
    'furnitureItems',
    'giftAid',
  ];
  const formattedPayload = { ...payload };
  if (payload?.address?.postcode) {
    formattedPayload.postcodeStub = trimPostcode(payload.address.postcode);
  }

  formFields.forEach(({ type, name }) => {
    if (type === fieldTypes.FILE_UPLOAD) {
      fieldsToInclude.push(name);
      formattedPayload[name].numberOfFilesUploaded =
        formattedPayload[name].files.length;
      delete formattedPayload[name].files;
    }
  });

  // Looks at the payload and checks against the inclusions list above.
  // If a field is not on the inclusions list
  // it deletes it from the stripped version of the payload sent to dataLayer
  Object.keys(formattedPayload).forEach((field) => {
    if (!fieldsToInclude.includes(field)) {
      delete formattedPayload[field];
    }
  });

  return formattedPayload;
};

const submit = async (
  apiUrl,
  values,
  formFields,
  captchaToken,
  setStatus,
  successCb,
  failureCb
) => {
  const payload = cloneDeep(values);

  payload.captchaToken = captchaToken || 'no_captcha';

  const { utmParams } = getAnalyticsParams();

  // If any utm params have been returned add them to the payload
  if (utmParams !== null) {
    payload.utm = utmParams;
  }

  await axios
    .post(apiUrl, formatPayload(payload, values, formFields))
    .then((res) => {
      if (res.status > 200 && res.status < 300) {
        // Clear out the server errors from status on a successful save.
        setStatus({ errors: {} });

        if (successCb) {
          successCb(res);
        }

        const { web_donor_id: webDonorId } = res.data.data;

        if (webDonorId) {
          setStorageValue('webDonorId', webDonorId, 'rich_text_replacements');
        } else {
          setStorageValue('webDonorId', '', 'rich_text_replacements');
        }

        sendEvent({
          event: 'formSubmitted',
          ...stripPayload(payload, formFields),
        });
      }
    })
    .catch((err) => {
      if (err.response?.data?.data) {
        const { errors } = err.response.data.data;

        /**
         * Use setStatus for server validation errors. Information at:
         * https://stackoverflow.com/a/54328567
         */
        setStatus({ errors });

        sendEvent({
          event: err.response.status === 422 ? 'formValidation' : 'submitError',
          formEntryId: payload.formEntryId,
          formName: payload.formName,
          formHeader: payload.formHeader,
          errors,
        });
      }
      if (failureCb) {
        failureCb(err);
      }
    });
};
export default submit;
